import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { ApolloError } from "@apollo/client";
import { t } from "@lingui/macro";
import { useBreakpoints } from "@otrium/core";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { Iso2LetterLocale, StoreKey } from "src/lib/Intl";
import { storeConfig, storeKey } from "src/config/app-config";
import { useRecalculateCart } from "src/hooks/useRecalculateCart";
import { useUpdateCartAddress } from "src/hooks/useUpdateCartAddress";
import { StoreConfig } from "src/utils/storeConfig";
import { CheckoutStepInfoSkeleton } from "src/molecules/CheckoutStepInfoSkeleton";
import { CheckoutSummary } from "src/molecules/CheckoutSummary";
import { CheckoutForm } from "src/molecules/CheckoutForm";
import { CheckoutOrderSummary } from "src/molecules/CheckoutOrderSummary";
import { AddressInput, Cart } from "src/types/ctgraphql.d";
import { Miscellaneous } from "src/types/graphql.d";
import { ShippingMethods } from "src/molecules/ShippingMethods";
import {
  getIsCheckoutValid,
  getIsShippingMethodValid,
  checkoutValidationSchema,
} from "src/utils/validationSchema";
import {
  getCartExtensionErrorInfo,
  getCheckoutSignUpPassword,
  setCheckoutCustomer,
} from "./utils";
import { useSegment } from "src/hooks/useSegment";
import { useSegmentIdentify } from "src/hooks/useSegmentIdentify";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useUpdateUserProfile } from "src/hooks/useUpdateUserProfile";
import { updateSession } from "src/utils/updateSession";
import { useLingui } from "@lingui/react";
import { TrackingShippingMethods } from "src/molecules/ShippingMethods/ShippingMethods";
import { SummaryWrapper } from "../CartView/CartView.styled";
import { getCartInfo } from "src/utils/ct/cart-helper";
import { usePageViewedContext } from "src/hooks/context/use-page-viewed-context";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { validateEmail } from "src/services/graphql/validate-email";
import {
  EMAIL_VALIDATED_STORAGE_KEY,
  INVALID_EMAIL_WARNING,
} from "./constants";
import { getStorageItem, setStorageItem } from "src/utils/helper/localStorage";
import { EmailValidationModal } from "./EmailValidationModal";
import styles from "./CheckoutView.module.scss";
import { signIn } from "src/utils/nextAuth";
import FORM_ERROR_MESSAGES from "src/messages/form";
import type { Address, Checkout } from "src/types/forms";
import { isSignUpAtCheckoutSchema } from "src/utils/type-guards";
import { SKIP_SIGN_IN_MESSAGE } from "src/utils/constants";

const getCheckoutAddress = (
  cart: Cart,
  addressType: "shippingAddress" | "billingAddress",
  session: Session,
  storeKey: StoreKey,
  storeConfig: StoreConfig
): Address & { isStreetNumberNotRequired: boolean } => {
  const isCOMStoreKey = storeKey === "OT-COM";
  const country = isCOMStoreKey
    ? ((cart[addressType]?.country ||
        cart.shippingAddress?.country) as Iso2LetterLocale)
    : storeConfig.shippingAddressCountryCode;

  const isCountryMatch = country === session.user.shipping_address?.country;
  const isUS = country === "US";
  const isFR = country === "FR";

  return {
    streetName:
      cart[addressType]?.streetName ||
      (isCountryMatch && session.user.shipping_address?.streetName) ||
      "",
    streetNumber: isUS
      ? ""
      : cart[addressType]?.streetNumber ||
        (isCountryMatch && session.user.shipping_address?.streetNumber) ||
        "",
    additionalStreetInfo: isFR
      ? ""
      : cart[addressType]?.additionalStreetInfo ||
        (isCountryMatch &&
          session.user.shipping_address?.additionalStreetInfo) ||
        "",
    postalCode:
      cart[addressType]?.postalCode ||
      (isCountryMatch && session.user.shipping_address?.postalCode) ||
      "",
    city:
      cart[addressType]?.city ||
      (isCountryMatch && session.user.shipping_address?.city) ||
      "",
    state: !isUS
      ? ""
      : cart[addressType]?.state ||
        (isCountryMatch && session.user.shipping_address?.state) ||
        "",
    country,
    isStreetNumberNotRequired: cart[addressType]?.streetNumber === "",
  };
};

const getCheckout = (
  cart: Cart,
  session: Session,
  storeKey: StoreKey,
  storeConfig: StoreConfig
): Checkout => ({
  firstName: cart.shippingAddress?.firstName || session.user.first_name || "",
  lastName: cart.shippingAddress?.lastName || session.user.last_name || "",
  email: session.user.email || cart.shippingAddress?.email || "",
  phone:
    cart.shippingAddress?.phone || session.user.shipping_address?.phone || "",
  shippingAddress: getCheckoutAddress(
    cart,
    "shippingAddress",
    session,
    storeKey,
    storeConfig
  ),
  billingAddress: getCheckoutAddress(
    cart,
    "billingAddress",
    session,
    storeKey,
    storeConfig
  ),
});

type CheckoutStepInfoProps = {
  cart: Cart | null;
  miscellaneous: Miscellaneous | undefined;
  setShowWarning: (showWarning: boolean) => void;
  setWarningMessage: (warningMessage: string) => void;
  onNextStep: () => void;
};

function CheckoutStepInfo({
  cart,
  miscellaneous,
  setShowWarning,
  setWarningMessage,
  onNextStep,
}: CheckoutStepInfoProps): JSX.Element {
  const { i18n } = useLingui();
  const { data: session } = useSession();
  const { isMobile } = useBreakpoints();
  const isCartRecalculatedRef = useRef(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);

  const displayEmailValidation = useMemo(
    () =>
      session?.user?.email &&
      (session?.user?.signInPlatform === AuthProvider.CREDENTIALS_REGISTER ||
        session?.user?.signInPlatform === AuthProvider.CREDENTIALS),
    [session?.user?.email, session?.user?.signInPlatform]
  );

  const isShippingMethodValid = getIsShippingMethodValid(cart);

  const [isShowCheckoutSummary, setIsShowCheckoutSummary] = useState(true);
  const [checkout, setCheckout] = useState<null | Checkout>(null);

  const [isUpdateUserProfile, setIsUpdateUserProfile] = useState<boolean>(true);
  const [isDifferentBillingAddress, setIsDifferentBillingAddress] =
    useState(false);
  const [loadingShipingMethods, setLoadingShipingMethods] =
    useState<boolean>(false);
  const [loadingUpdateCartCountry, setLoadingUpdateCartCountry] =
    useState<boolean>(false);
  const [trackingShippingMethods, setTrackingShippingMethods] =
    useState<TrackingShippingMethods>();

  const { pageViewTracked } = usePageViewedContext();

  const isLoggedIn = useIsLoggedIn();

  const {
    user: analyticsUser,
    checkoutStarted,
    checkoutFirstStepCompleted,
    segmentCheckoutSignedUp,
  } = useSegment();

  const { segmentIdentify } = useSegmentIdentify();

  const getErrorMessageAndShowWarning = (
    error: ApolloError,
    locale = "en",
    showWarning: boolean
  ) => {
    const errorInfo = getCartExtensionErrorInfo(error);
    const fallbackMessage = errorInfo?.message ?? "Unexpected error";
    const localizedMessage = errorInfo?.localizedMessage;

    const message =
      (localizedMessage && localizedMessage[locale]) || fallbackMessage;

    if (message && showWarning) {
      setWarningMessage(message);
      setShowWarning(true);
    }
  };

  const cartInfo = useMemo(() => getCartInfo(cart), [cart]);

  const { loading: isCartRecalculating, recalculateCart } = useRecalculateCart({
    onError: (error: ApolloError) => {
      getErrorMessageAndShowWarning(error, storeConfig.locale, true);
    },
  });

  const { loading: loadingUpdateCartAddress, updateCartAddress } =
    useUpdateCartAddress({
      onError: (error: ApolloError) => {
        getErrorMessageAndShowWarning(error, storeConfig.locale, true);
      },
    });

  const { updateUserProfile } = useUpdateUserProfile({
    onCompleted: (data) => {
      if (data.updateUserProfile?.token) {
        void updateSession({ token: data.updateUserProfile.token });
      }
    },
  });

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      ...checkoutValidationSchema,
      shouldCreateAccount: Yup.boolean(),
      password: Yup.string()
        .test({
          test(value, { parent }) {
            if (
              isSignUpAtCheckoutSchema(parent) &&
              parent.shouldCreateAccount &&
              !value
            ) {
              return false;
            }

            return true;
          },
          message: FORM_ERROR_MESSAGES.passwordRequired,
        })
        .min(6, FORM_ERROR_MESSAGES.passwordLength)
        .matches(/^\S+$/, { message: FORM_ERROR_MESSAGES.passwordSpace }),
    });
  }, []);

  useEffect(() => {
    window.scrollTo({
      behavior: "auto",
      top: 0,
    });
  }, []);

  useEffect(() => {
    if (
      !cart ||
      !cartInfo ||
      isCartRecalculating ||
      isCartRecalculatedRef.current
    ) {
      return;
    }
    isCartRecalculatedRef.current = true;

    void recalculateCart(cartInfo);
  }, [cart, cartInfo, isCartRecalculating, recalculateCart]);

  useEffect(() => {
    if (!cart || !session || checkout) {
      return;
    }

    setCheckout(getCheckout(cart, session, storeKey, storeConfig));
  }, [checkout, setCheckout, cart, session]);

  useEffect(() => {
    if (!checkout) {
      return;
    }

    setIsShowCheckoutSummary(
      getIsCheckoutValid(checkout) &&
        session?.user?.signInPlatform !== AuthProvider.GUEST_LOGIN
    );
  }, [checkout, session?.user?.signInPlatform, setIsShowCheckoutSummary]);

  const checkoutStartedTrackerRef = useRef(false);
  useEffect(() => {
    if (
      !cart ||
      !trackingShippingMethods?.length ||
      !pageViewTracked ||
      checkoutStartedTrackerRef.current
    ) {
      return;
    }

    void checkoutStarted(cart, trackingShippingMethods);

    checkoutStartedTrackerRef.current = true;
  }, [cart, trackingShippingMethods, pageViewTracked, checkoutStarted]);

  const trackOnStepSubmit = useCallback(
    async (
      email: string,
      billingAddress: AddressInput,
      shippingAddress?: AddressInput
    ) => {
      const htUser = analyticsUser;
      try {
        const anonymousId = htUser?.anonymousId?.() ?? "";

        if (!anonymousId) {
          const err = new Error(
            "`anonymousId` not returned for user with email: " + email
          );
          console.error(err);
        } else if (!email) {
          const err = new Error(
            "`email` not returned for user with anonymousId: " + anonymousId
          );
          console.error(err);
        } else {
          await segmentIdentify(anonymousId, email);
        }

        if (cart) {
          await checkoutFirstStepCompleted({
            cartId: cart.id,
            shippingMethodId: cart.shippingInfo?.shippingMethod?.id,
            shippingMethodName: cart.shippingInfo?.shippingMethod?.name,
            email,
            billingAddress,
            shippingAddress,
          });
        }
      } catch (e) {
        throw e;
      }
    },
    [cart, analyticsUser, checkoutFirstStepCompleted, segmentIdentify]
  );

  const handleFormSubmission = useCallback(
    async (formik: FormikProps<Checkout>) => {
      const isEmailValidated = getStorageItem(EMAIL_VALIDATED_STORAGE_KEY);

      if (
        (isEmailValidated === "1" &&
          session?.user?.signInPlatform === AuthProvider.GUEST_LOGIN) ||
        (session?.user?.signInPlatform !== AuthProvider.CREDENTIALS_REGISTER &&
          session?.user?.signInPlatform !== AuthProvider.CREDENTIALS &&
          session?.user?.signInPlatform !== AuthProvider.GUEST_LOGIN)
      ) {
        await formik.submitForm();
        return;
      }

      const { isValid } = await validateEmail(formik.values.email);

      if (
        !isValid &&
        (session?.user?.signInPlatform === AuthProvider.CREDENTIALS_REGISTER ||
          session?.user?.signInPlatform === AuthProvider.CREDENTIALS)
      ) {
        setIsEmailInvalid(true);
        return;
      }

      if (
        !isValid &&
        session?.user?.signInPlatform === AuthProvider.GUEST_LOGIN
      ) {
        formik.setFieldError("email", INVALID_EMAIL_WARNING);

        setStorageItem(EMAIL_VALIDATED_STORAGE_KEY, "1");

        return;
      }

      await formik.submitForm();
    },
    [session?.user?.signInPlatform, setIsEmailInvalid]
  );

  const onSubmit = useCallback(
    async (values: Checkout) => {
      setShowWarning(false);

      const trimmedEmail = values.email.trim();
      const firstName = values.firstName.trim() || "";
      const lastName = values.lastName.trim() || "";

      const shippingAddressValues = {
        streetName: values.shippingAddress.streetName.trim(),
        streetNumber: values.shippingAddress.streetNumber.trim(),
        additionalStreetInfo:
          values.shippingAddress.additionalStreetInfo.trim(),
        postalCode: values.shippingAddress.postalCode.trim(),
        city: values.shippingAddress.city.trim(),
        state: values.shippingAddress.state.trim(),
        country: values.shippingAddress.country.trim(),
      };

      const shippingAddress: AddressInput = {
        firstName,
        lastName,
        email: trimmedEmail,
        phone: values.phone.trim(),
        ...shippingAddressValues,
      };
      const billingAddress: AddressInput = {
        firstName,
        lastName,
        email: trimmedEmail,
        phone: values.phone.trim(),
        streetName: values.billingAddress.streetName.trim(),
        streetNumber: values.billingAddress.streetNumber.trim(),
        additionalStreetInfo: values.billingAddress.additionalStreetInfo.trim(),
        postalCode: values.billingAddress.postalCode.trim(),
        city: values.billingAddress.city.trim(),
        state: values.billingAddress.state.trim(),
        country: values.billingAddress.country.trim(),
      };

      const signUpPassword = getCheckoutSignUpPassword(values);

      try {
        if (signUpPassword) {
          const result = await signIn(AuthProvider.CREDENTIALS_REGISTER, {
            redirect: false,
            ...{
              name: `${firstName} ${lastName}`,
              email: trimmedEmail,
              password: signUpPassword,
            },
            skipSignIn: true,
          });

          if (
            result?.error &&
            result.error === SKIP_SIGN_IN_MESSAGE.WITH_TRACKING
          ) {
            void segmentCheckoutSignedUp({ email: trimmedEmail });
          }
        }
      } catch (e) {
        console.error("An error occurred while registering the account.", e);
      }

      try {
        const updateCartResponse = await updateCartAddress(
          shippingAddress,
          billingAddress,
          trimmedEmail,
          cartInfo
        );

        if (!updateCartResponse?.data?.updateMyCart) {
          return;
        }

        await trackOnStepSubmit(
          trimmedEmail,
          billingAddress,
          isDifferentBillingAddress ? shippingAddress : undefined
        );

        setCheckoutCustomer({
          firstName,
          lastName,
          email: trimmedEmail,
          usedSignUpBlock: !!signUpPassword,
        });

        if (isUpdateUserProfile && isLoggedIn) {
          try {
            await updateUserProfile({
              shipping_address: {
                phone: values.phone.trim(),
                ...shippingAddressValues,
              },
            });
          } catch (e) {
            console.error(e);
          }
        }

        void onNextStep();
      } catch (e) {
        const err = new Error(
          "An error occured while submitting 'Checkout first step'"
        );

        if (e instanceof Error) {
          err.cause = e;
        }

        console.error(err);
      }
    },
    [
      cartInfo,
      isDifferentBillingAddress,
      isUpdateUserProfile,
      isLoggedIn,
      setShowWarning,
      updateCartAddress,
      trackOnStepSubmit,
      updateUserProfile,
      onNextStep,
      segmentCheckoutSignedUp,
    ]
  );

  const emailValidationOnClose = useCallback(() => {
    setIsEmailInvalid(false);
  }, []);

  if (!checkout || isCartRecalculating) {
    return <CheckoutStepInfoSkeleton />;
  }

  return (
    <Formik
      initialValues={checkout}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <>
          <div className={styles.formContainer}>
            {isShowCheckoutSummary ? (
              <CheckoutSummary
                checkout={checkout}
                onClickEdit={() => setIsShowCheckoutSummary(false)}
              />
            ) : (
              <CheckoutForm
                formik={formik}
                isUpdateUserProfile={isUpdateUserProfile}
                setIsUpdateUserProfile={setIsUpdateUserProfile}
                onDifferentBillingAddressToggle={setIsDifferentBillingAddress}
                setLoadingUpdateCartCountry={setLoadingUpdateCartCountry}
                cartInfo={cartInfo}
              />
            )}

            <ShippingMethods
              formik={formik}
              cart={cart}
              setLoadingShipingMethods={setLoadingShipingMethods}
              setTrackingShippingMethods={setTrackingShippingMethods}
              trackingShippingMethods={trackingShippingMethods}
            />
          </div>

          {isMobile ? (
            <CheckoutOrderSummary
              cart={cart}
              miscellaneous={miscellaneous}
              step={1}
              buttonLabel={t(i18n)`Continue to payment`}
              buttonDisabled={
                (!formik.isValid &&
                  formik.errors.email !== INVALID_EMAIL_WARNING) ||
                !cart?.shippingInfo?.shippingMethod?.id ||
                !isShippingMethodValid ||
                loadingUpdateCartAddress ||
                loadingShipingMethods ||
                loadingUpdateCartCountry ||
                formik.isSubmitting
              }
              buttonLoading={loadingUpdateCartAddress || formik.isSubmitting}
              onClick={() => void handleFormSubmission(formik)}
            />
          ) : (
            <SummaryWrapper>
              <CheckoutOrderSummary
                cart={cart}
                miscellaneous={miscellaneous}
                step={1}
                buttonLabel={t(i18n)`Continue to payment`}
                buttonDisabled={
                  (!formik.isValid &&
                    formik.errors.email !== INVALID_EMAIL_WARNING) ||
                  !cart?.shippingInfo?.shippingMethod?.id ||
                  !isShippingMethodValid ||
                  loadingUpdateCartAddress ||
                  loadingShipingMethods ||
                  loadingUpdateCartCountry ||
                  formik.isSubmitting
                }
                buttonLoading={loadingUpdateCartAddress || formik.isSubmitting}
                onClick={() => void handleFormSubmission(formik)}
              />
            </SummaryWrapper>
          )}

          {displayEmailValidation ? (
            <EmailValidationModal
              isOpen={isEmailInvalid}
              onClose={emailValidationOnClose}
              onProceedToCheckout={() => {
                setIsEmailInvalid(false);

                void formik.submitForm();
              }}
            />
          ) : null}
        </>
      )}
    </Formik>
  );
}

export { CheckoutStepInfo };
